<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Receivables</span>
      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

    <form class="px-6 pb-5 pt-2" @submit.prevent="generateReport">
      <div class="flex flex-col md:flex-row md:items-end md:flex-wrap gap-x-4 gap-y-2">
        <div class="flex flex-col gap-1 w-full md:w-auto md:min-w-[180px]">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">From</label>
          <input
            v-model="form.fromDate"
            type="date"
            class="px-2.5 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
          />
        </div>

        <div class="flex flex-col gap-1 w-full md:w-auto md:min-w-[180px]">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Upto</label>
          <input
            v-model="form.uptoDate"
            type="date"
            class="px-2.5 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
          />
        </div>

        <div class="flex flex-col gap-1 w-full md:w-auto md:min-w-[220px]">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Select Company</label>
          <select
            v-model="form.deliveryCompanyId"
            class="px-2.5 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
          >
            <option value="">All Companies</option>
            <option v-for="dc in deliveryCompanies" :key="dc.id" :value="String(dc.id)">{{ dc.name }}</option>
          </select>
        </div>

        <div class="flex flex-col gap-1 w-full md:w-auto">
          <button
            type="submit"
            :disabled="isLoadingReport"
            class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span v-if="isLoadingReport" class="inline-flex items-center gap-2">
              <span class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></span>
              Generating...
            </span>
            <span v-else>Generate</span>
          </button>
        </div>
      </div>
    </form>

    <div class="px-2 pb-6 pt-2">
      <template v-if="showReportTable">
        <div v-if="isLoadingReport" class="py-12 flex justify-center items-center">
          <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <div v-else class="w-full">
          <p class="text-[0.78rem] font-semibold text-[#1e293b] mb-3 m-0 px-4">
            {{ reportPeriodTitle }}
          </p>

          <div class="receivables-table-scroll relative overflow-auto min-h-[220px] max-h-[calc(100vh-260px)] px-2 custom-scrollbar">
            <table class="w-full receivables-table border-collapse">
              <thead>
                <tr>
                  <th class="col-sn">No.</th>
                  <th class="col-date">Date</th>
                  <th class="col-company">Company Name</th>
                  <th class="num">Credit Amount</th>
                  <th class="num">Paid Amount</th>
                  <th class="num">Unpaid Amount</th>
                  <th class="col-action">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, idx) in reportRows"
                  :key="'recv-' + row.id"
                  class="hover:bg-slate-50 transition-colors border-b"
                >
                  <td class="col-sn">{{ idx + 1 }}</td>
                  <td class="col-date">{{ formatDisplayDate(row.postingDate) }}</td>
                  <td class="col-company">{{ deliveryCompanyName(row.deliveryId) }}</td>
                  <td class="num text-slate-700">{{ fmt(row.creditAmount) }}</td>
                  <td class="num text-slate-700">{{ fmt(row.paidAmount) }}</td>
                  <td class="num font-semibold text-[#0d4169]">{{ fmt(row.unpaidAmount) }}</td>
                  <td class="col-action actions">
                    <button
                      type="button"
                      class="text-emerald-600 edit-btn hover:underline"
                      :disabled="isLoadingDetail && selectedDetailRowId === row.id"
                      @click="showRowDetail(row)"
                    >
                      <span v-if="isLoadingDetail && selectedDetailRowId === row.id">Loading...</span>
                      <span v-else>Detail</span>
                    </button>
                  </td>
                </tr>
                <tr v-if="reportRows.length === 0">
                  <td colspan="7" class="text-center text-slate-400 italic py-5">
                    No unpaid receivables found for the selected filters.
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="reportRows.length > 0">
                <tr class="font-semibold receivables-tfoot-row">
                  <td colspan="3" class="receivables-total-label">Total</td>
                  <td class="num">{{ fmt(totals.creditAmount) }}</td>
                  <td class="num">{{ fmt(totals.paidAmount) }}</td>
                  <td class="num font-semibold text-[#0d4169]">{{ fmt(totals.unpaidAmount) }}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </template>

      <p
        v-else
        class="text-sm text-slate-500 text-center py-10 m-0 italic"
      >
        Select filters and click Generate to view receivables.
      </p>
    </div>
  </div>

  <Teleport to="body">
    <div v-if="isDetailModalOpen && selectedRowDetail">
      <div class="receivables-modal-backdrop" @click.self="isDetailModalOpen = false">
        <div class="receivables-modal">
        <div class="receivables-modal-header">
          <div>
            <p class="receivables-modal-title">Receivable Detail</p>
            <p class="receivables-modal-subtitle">
              Date: {{ formatDisplayDate(selectedRowHeader?.postingDate || selectedRowDetail.postingDate) }}
              ·
              Company: {{ deliveryCompanyName(selectedRowHeader?.deliveryId || selectedRowDetail.deliveryId) }}
            </p>
          </div>
          <button type="button" class="receivables-modal-close" @click="isDetailModalOpen = false">
            ✕
          </button>
        </div>

        <div class="receivables-modal-body">
          <div class="receivables-summary-grid">
            <div class="receivables-summary-item">
              <span class="label">Credit Amount</span>
              <span class="value highlight">{{ fmt(selectedRowHeader?.creditAmount) }}</span>
            </div>
            <div class="receivables-summary-item">
              <span class="label">Paid Amount</span>
              <span class="value">{{ fmt(selectedRowHeader?.paidAmount) }}</span>
            </div>
            <div class="receivables-summary-item">
              <span class="label">Unpaid Amount</span>
              <span class="value highlight">{{ fmt(selectedRowHeader?.unpaidAmount) }}</span>
            </div>
            <div class="receivables-summary-item">
              <span class="label">Total Before</span>
              <span class="value">{{ fmt(selectedRowHeader?.totalBefore) }}</span>
            </div>
            <div class="receivables-summary-item">
              <span class="label">VAT (Sys)</span>
              <span class="value">{{ fmt(selectedRowHeader?.vatSys) }}</span>
            </div>
            <div class="receivables-summary-item">
              <span class="label">VAT (Man)</span>
              <span class="value">{{ fmt(selectedRowHeader?.vatMan) }}</span>
            </div>
            <div class="receivables-summary-item">
              <span class="label">Total Sales</span>
              <span class="value">{{ fmt(selectedRowHeader?.totalSales) }}</span>
            </div>
            <div class="receivables-summary-item" v-if="selectedRowHeader?.remark">
              <span class="label">Remark</span>
              <span class="value">{{ selectedRowHeader.remark }}</span>
            </div>
          </div>

          <div class="receivables-section-title">Bank Deposits</div>
          <div class="receivables-detail-table-wrap receivables-bank-table-wrap">
            <table class="w-full receivables-table border-collapse">
              <thead>
                <tr>
                  <th class="col-sn">No.</th>
                  <th class="col-company">Bank Name</th>
                  <th class="num">Amount</th>
                  <th class="col-date">Date</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(deposit, idx) in selectedBankDeposits"
                  :key="'bank-' + idx + '-' + deposit.bankId"
                  class="hover:bg-slate-50 transition-colors border-b"
                >
                  <td class="col-sn">{{ idx + 1 }}</td>
                  <td class="col-company">{{ deposit.bankName || '—' }}</td>
                  <td class="num">{{ fmt(deposit.amount) }}</td>
                  <td class="col-date">{{ formatPaidDate(deposit.paidDate) }}</td>
                </tr>
                <tr v-if="selectedBankDeposits.length === 0">
                  <td colspan="4" class="text-center text-slate-400 italic py-5">
                    No bank deposits recorded.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="receivables-section-title">Income Details</div>
          <div class="receivables-detail-table-wrap receivables-income-detail-wrap custom-scrollbar">
            <table class="receivables-income-detail-table min-w-max">
              <thead>
                <tr>
                  <th class="col-sn">S/N</th>
                  <th class="col-income">Income</th>
                  <th class="num">Sales<br>(Sys) Taxable</th>
                  <th class="num">Sales<br>(Man) Taxable</th>
                  <th class="num">Total<br>Taxable</th>
                  <th class="num">Sales<br>(Sys) Non-Taxable</th>
                  <th class="num">Sales<br>(Man) Non-Taxable</th>
                  <th class="num">Total<br>Non-Taxable</th>
                  <th class="num">Sales (Sys)<br>Taxable SC</th>
                  <th class="num">Sales (Man)<br>Taxable SC</th>
                  <th class="num">Total<br>Taxable SC</th>
                  <th class="num">Sales (Sys)<br>Non-Taxable SC</th>
                  <th class="num">Sales (Man)<br>Non-Taxable SC</th>
                  <th class="num">Total<br>Non-Taxable SC</th>
                  <th class="num">Total<br>Before</th>
                  <th class="num">VAT<br>(Sys)</th>
                  <th class="num">VAT<br>(Man)</th>
                  <th class="num">Variation</th>
                  <th class="num">Total<br>VAT</th>
                  <th class="num">Total<br>Sales</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(detail, idx) in visibleIncomeDetails"
                  :key="'detail-' + detail.id + '-' + idx"
                  class="hover:bg-slate-50 transition-colors border-b"
                >
                  <td class="col-sn">{{ idx + 1 }}</td>
                  <td class="col-income">{{ detail.categoryName || '—' }}</td>
                  <td class="num">{{ fmt(detail.salesSysTaxable) }}</td>
                  <td class="num">{{ fmt(detail.salesManTaxable) }}</td>
                  <td class="num">{{ fmt(detail.totalTaxable) }}</td>
                  <td class="num">{{ fmt(detail.salesSysNoTax) }}</td>
                  <td class="num">{{ fmt(detail.salesManNoTax) }}</td>
                  <td class="num">{{ fmt(detail.totalNonTaxable) }}</td>
                  <td class="num">{{ fmt(detail.salesSysTaxableSc) }}</td>
                  <td class="num">{{ fmt(detail.salesManTaxableSc) }}</td>
                  <td class="num">{{ fmt(detail.totalTaxableSc) }}</td>
                  <td class="num">{{ fmt(detail.salesSysNoTaxSc) }}</td>
                  <td class="num">{{ fmt(detail.salesManNoTaxSc) }}</td>
                  <td class="num">{{ fmt(detail.totalNonTaxableSc) }}</td>
                  <td class="num">{{ fmt(detail.totalBefore) }}</td>
                  <td class="num">{{ fmt(detail.vatSys) }}</td>
                  <td class="num">{{ fmt(detail.vatMan) }}</td>
                  <td class="num">{{ fmt(detailVatManVariation(detail)) }}</td>
                  <td class="num">{{ fmt(detail.totalVat) }}</td>
                  <td class="num font-semibold text-[#0d4169]">{{ fmt(detail.totalSales) }}</td>
                </tr>
                <tr v-if="visibleIncomeDetails.length === 0">
                  <td colspan="20" class="text-center text-slate-400 italic py-5">
                    No income detail rows found.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="receivables-modal-footer">
          <button type="button" class="modal-close-btn" @click="isDetailModalOpen = false">
            Close
          </button>
        </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import FinanceToast from '../FinanceToast.vue'
import { apiFetch } from '../../../utils/api'

const store = useStore()

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const resolvedCompanyName = computed(() => {
  const user = currentUser.value || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const form = reactive({
  fromDate: '',
  uptoDate: '',
  deliveryCompanyId: ''
})

const toast = reactive({
  show: false,
  message: '',
  variant: 'success'
})
let toastTimer = null

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.message = message
  toast.variant = variant
  toast.show = true
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
    toastTimer = null
  }, duration)
}

const deliveryCompanies = ref([])
const reportRows = ref([])
const isLoadingReport = ref(false)
const isLoadingDetail = ref(false)
const selectedDetailRowId = ref('')
const selectedRowDetail = ref(null)
const selectedRowDetails = ref([])
const selectedRowHeader = ref(null)
const isDetailModalOpen = ref(false)
const hasGenerated = ref(false)
const companyConstants = ref({ tax: 15, addition: 10 })
let reportRequestId = 0

const showReportTable = computed(() => hasGenerated.value)

const deliveryCompanyMap = computed(() =>
  new Map(deliveryCompanies.value.map((dc) => [String(dc.id), String(dc.name || '').trim()]))
)

function formatDisplayDate(isoDate) {
  if (!isoDate) return '—'
  const d = new Date(`${isoDate}T00:00:00`)
  if (Number.isNaN(d.getTime())) return isoDate
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

const reportPeriodTitle = computed(() => {
  const companyLabel = form.deliveryCompanyId
    ? deliveryCompanyName(form.deliveryCompanyId)
    : 'All Companies'

  if (form.fromDate && form.uptoDate) {
    return `Unpaid receivables for ${formatDisplayDate(form.fromDate)} - ${formatDisplayDate(form.uptoDate)} (${companyLabel})`
  }
  if (form.fromDate) {
    return `Unpaid receivables from ${formatDisplayDate(form.fromDate)} (${companyLabel})`
  }
  if (form.uptoDate) {
    return `Unpaid receivables up to ${formatDisplayDate(form.uptoDate)} (${companyLabel})`
  }
  return `Unpaid receivables (${companyLabel})`
})

function n(v) {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function fmt(x) {
  return n(x).toFixed(2)
}

function sumField(field) {
  return reportRows.value.reduce((acc, row) => acc + n(row?.[field]), 0)
}

const totals = computed(() => ({
  creditAmount: sumField('creditAmount'),
  paidAmount: sumField('paidAmount'),
  unpaidAmount: sumField('unpaidAmount')
}))

const selectedBankDeposits = computed(() => {
  const list = selectedRowHeader.value?.bankDeposits
  return Array.isArray(list) ? list : []
})

const visibleIncomeDetails = computed(() =>
  selectedRowDetails.value.filter((detail) => n(detail.totalSales) !== 0)
)

function formatPaidDate(rawDate) {
  const value = String(rawDate || '').trim()
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

function manAdditionRate() {
  return Math.max(0, n(companyConstants.value.addition)) / 100
}

function manTaxRate() {
  return Math.max(0, n(companyConstants.value.tax)) / 100
}

function calcManTaxableSc(taxable) {
  return Math.round(n(taxable) * manAdditionRate() * 100) / 100
}

function calcManVat(taxable, taxableSc) {
  const base = n(taxable) + n(taxableSc)
  return Math.round(base * manTaxRate() * 100) / 100
}

function detailVatManCalculated(detail) {
  const taxable = n(detail?.salesManTaxable)
  const taxableSc = calcManTaxableSc(taxable)
  return calcManVat(taxable, taxableSc)
}

function detailVatManVariation(detail) {
  return Math.round((detailVatManCalculated(detail) - n(detail?.vatMan)) * 100) / 100
}

async function loadCompanyConstants() {
  try {
    const res = await apiFetch('/settings/company-constants')
    if (!res.ok) return
    const data = await res.json().catch(() => ({}))
    companyConstants.value = {
      tax: Number(data?.tax ?? 15),
      addition: Number(data?.addition ?? 10),
    }
  } catch {
    // keep defaults
  }
}

function deliveryCompanyName(deliveryId) {
  const key = String(deliveryId || '').trim()
  if (!key) return '—'
  return deliveryCompanyMap.value.get(key) || `Company #${key}`
}

async function loadDeliveryCompanies() {
  try {
    const res = await apiFetch('/settings/delivery-companies')
    if (!res.ok) {
      deliveryCompanies.value = []
      return
    }
    const data = await res.json().catch(() => [])
    deliveryCompanies.value = Array.isArray(data)
      ? data
        .map((c) => ({
          id: c.id,
          name: String(c?.name || '').trim(),
          isActive: c?.isActive
        }))
        .filter((c) => c.id != null && c.name !== '')
        .sort((a, b) => a.name.localeCompare(b.name))
      : []
  } catch {
    deliveryCompanies.value = []
  }
}

async function fetchReceivables(fromDate, uptoDate, deliveryId = '') {
  const from = String(fromDate || '').trim()
  const upto = String(uptoDate || '').trim()
  const qs = new URLSearchParams()
  if (from) qs.set('startDate', from)
  if (upto) {
    qs.set('endDate', upto)
  } else if (from) {
    qs.set('endDate', from)
  }
  const deliveryKey = String(deliveryId || '').trim()
  if (deliveryKey) qs.set('deliveryId', deliveryKey)
  const query = qs.toString()
  const res = await apiFetch(`/finance/receivables${query ? `?${query}` : ''}`)
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to load receivables.')
  }
  const list = await res.json().catch(() => [])
  return Array.isArray(list) ? list : []
}

async function fetchReceivableDetail(savedTotalIncomeId) {
  const id = String(savedTotalIncomeId || '').trim()
  const res = await apiFetch(`/finance/receivables/${encodeURIComponent(id)}/details`)
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to load receivable detail.')
  }
  return res.json()
}

async function showRowDetail(row) {
  if (!row?.id) {
    showToast('Invalid row detail request.', 'error')
    return
  }
  selectedDetailRowId.value = row.id
  isLoadingDetail.value = true
  try {
    const data = await fetchReceivableDetail(row.id)
    selectedRowDetail.value = row
    selectedRowHeader.value = data?.header || null
    selectedRowDetails.value = Array.isArray(data?.details) ? data.details : []
    isDetailModalOpen.value = true
  } catch (e) {
    selectedRowDetail.value = null
    selectedRowDetails.value = []
    selectedRowHeader.value = null
    isDetailModalOpen.value = false
    showToast(e?.message || 'Failed to load receivable detail.', 'error')
  } finally {
    isLoadingDetail.value = false
    selectedDetailRowId.value = ''
  }
}

async function generateReport() {
  const fromDate = String(form.fromDate || '').trim()
  const uptoDate = String(form.uptoDate || '').trim()
  if (fromDate && uptoDate && uptoDate < fromDate) {
    showToast('Upto date cannot be before From date.', 'error')
    return
  }

  const requestId = ++reportRequestId
  isLoadingReport.value = true
  hasGenerated.value = true
  selectedRowDetail.value = null
  selectedRowDetails.value = []
  selectedRowHeader.value = null
  isDetailModalOpen.value = false

  try {
    const list = await fetchReceivables(fromDate, uptoDate, form.deliveryCompanyId)
    if (requestId !== reportRequestId) return
    reportRows.value = list
  } catch (e) {
    if (requestId !== reportRequestId) return
    reportRows.value = []
    showToast(e?.message || 'Failed to load receivables.', 'error')
  } finally {
    if (requestId === reportRequestId) {
      isLoadingReport.value = false
    }
  }
}

onMounted(async () => {
  await Promise.all([loadDeliveryCompanies(), loadCompanyConstants()])
})
</script>

<style scoped>
.receivables-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  width: 100%;
}

.receivables-table th,
.receivables-table td {
  padding: 6px 12px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  font-size: 0.72rem;
  line-height: 1.2;
  width: calc(100% / 7);
}

.receivables-table th {
  background: #f1f5f9;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.receivables-table th.col-sn,
.receivables-table td.col-sn,
.receivables-table th.col-date,
.receivables-table td.col-date,
.receivables-table th.col-company,
.receivables-table td.col-company,
.receivables-table th.col-action,
.receivables-table td.col-action {
  text-align: left;
}

.col-date {
  white-space: nowrap;
}

.col-company {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.receivables-table th.num,
.receivables-table td.num {
  text-align: center;
  white-space: nowrap;
}

.receivables-table tbody tr:hover td {
  background-color: #f8fafc;
}

.receivables-table tfoot td {
  background: #f1f5f9;
  border-top: 2px solid #cbd5e1;
  font-weight: 600;
}

.receivables-table tfoot td.receivables-total-label {
  text-align: left;
}

.actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.actions button {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 11px;
  padding: 0;
  display: flex;
  align-items: center;
}

.actions .edit-btn {
  font-size: 0.85rem;
}

.receivables-modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem;
  overflow-y: auto;
  z-index: 10000;
}

.receivables-modal {
  background: #ffffff;
  border-radius: 0.75rem;
  box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.35);
  max-width: min(1200px, 96vw);
  width: 100%;
  max-height: calc(100vh - 4rem);
  margin: auto 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  z-index: 10001;
}

.receivables-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #0a3354;
  background: #0d4169;
}

.receivables-modal-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #ffffff;
}

.receivables-modal-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.75rem;
  color: #dbeafe;
}

.receivables-modal-close {
  border: none;
  background: transparent;
  font-size: 0.95rem;
  color: #ffffff;
  cursor: pointer;
  padding: 0.2rem 0.35rem;
  border-radius: 9999px;
  line-height: 1;
}

.receivables-modal-close:hover {
  background: rgba(255, 255, 255, 0.12);
}

.receivables-modal-body {
  padding: 1rem 1.5rem 0.75rem;
  overflow: auto;
}

.receivables-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem 1rem;
  margin-bottom: 0.75rem;
}

.receivables-summary-item .label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
}

.receivables-summary-item .value {
  display: block;
  font-size: 0.78rem;
  color: #111827;
}

.receivables-summary-item .value.highlight {
  color: #0d4169;
  font-weight: 700;
}

.receivables-section-title {
  margin: 0.75rem 0 0.5rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: #0d4169;
}

.receivables-detail-table-wrap {
  max-height: 220px;
  overflow: auto;
}

.receivables-bank-table-wrap {
  max-height: 160px;
  margin-bottom: 0.25rem;
}

.receivables-income-detail-wrap {
  max-height: 320px;
  overflow: auto;
}

.receivables-income-detail-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.72rem;
  color: #1e293b;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: auto;
  width: max-content;
  min-width: 100%;
}

.receivables-income-detail-table th,
.receivables-income-detail-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  white-space: nowrap;
}

.receivables-income-detail-table th {
  background: #f1f5f9;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 2;
}

.receivables-income-detail-table th.col-sn,
.receivables-income-detail-table td.col-sn,
.receivables-income-detail-table th.col-income,
.receivables-income-detail-table td.col-income {
  text-align: left;
}

.receivables-income-detail-table th.num,
.receivables-income-detail-table td.num {
  text-align: center;
}

.receivables-income-detail-table .col-income {
  min-width: 8rem;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.receivables-modal-footer {
  padding: 0.75rem 1.5rem 1rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

.modal-close-btn {
  padding: 0.4rem 1rem;
  font-size: 0.8rem;
  border-radius: 0.375rem;
  border: 1px solid #94a3b8;
  background: #f8fafc;
  color: #334155;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.modal-close-btn:hover {
  background: #e5e7eb;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
